import { useState, useMemo, useEffect, useCallback } from 'react';
import Agriculture from '@mui/icons-material/Agriculture';
import {
	SelectionModal,
	SelectionCard,
	LocationIcon,
	FarmIcon,
} from '@practium/features/parcelling';
import { fuzzyFilter } from '@practium/shared';
import type { FieldDiaryFarm } from '../../types';
import { formatFarmLocation } from '../../utils/farm.utils';
import gridStyles from './FarmSelectionGrid.module.css';

const SKELETON_CARDS = 4;
import { TableEmptyState } from '@practium/ui';
import { FarmCardSkeleton } from '../Skeletons/FarmCardSkeleton';

export interface FarmSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelect: (farm: FieldDiaryFarm) => void;
	farms: FieldDiaryFarm[];
	loading?: boolean;
}

export const FarmSelectionModal = ({
	isOpen,
	onClose,
	onSelect,
	farms,
	loading = false,
}: FarmSelectionModalProps) => {
	const [search, setSearch] = useState('');

	useEffect(() => {
		if (isOpen) setSearch('');
	}, [isOpen]);

	const filteredFarms = useMemo(() => {
		if (!search.trim()) return farms;
		return fuzzyFilter(farms, search, (f) => `${f.name} ${f.city} ${f.state}`);
	}, [farms, search]);

	const handleSelect = useCallback(
		(farm: FieldDiaryFarm) => {
			onSelect(farm);
		},
		[onSelect, onClose]
	);

	return (
		<SelectionModal
			isOpen={isOpen}
			title="Selecione a fazenda para ver o diário de campo"
			searchPlaceholder="Buscar por nome, cidade ou estado..."
			searchValue={search}
			onSearchChange={setSearch}
			onClose={onClose}
		>
			<div className={gridStyles.grid}>
				{loading &&
					Array.from({ length: SKELETON_CARDS }, (_, index) => <FarmCardSkeleton key={index} />)}
				{!loading &&
					filteredFarms.map((farm) => (
						<SelectionCard
							key={farm._id}
							name={farm.name}
							location={formatFarmLocation(farm)}
							icon={
								<div className={gridStyles.iconWrapper}>
									{farm.avatarUrl ? (
										<img src={farm.avatarUrl} alt={farm.name} className={gridStyles.farmAvatar} />
									) : (
										<Agriculture className={gridStyles.farmIcon} />
									)}
								</div>
							}
							locationIcon={<LocationIcon />}
							farmInfo={farm.area ? `${farm.area} hectares` : undefined}
							farmIcon={<FarmIcon />}
							selected={false}
							onClick={() => handleSelect(farm)}
						/>
					))}
				{!loading && filteredFarms.length === 0 && (
					<div className={gridStyles.empty}>
						<TableEmptyState
							title="Nenhuma fazenda encontrada"
							subtitle={
								farms.length === 0
									? 'Cadastre uma propriedade para usar o diário de campo'
									: 'Tente buscar por outro nome, cidade ou estado'
							}
						/>
					</div>
				)}
			</div>
		</SelectionModal>
	);
};
